"use client";

import Link from "@/components/Link";
import { IconExternalLink, IconTags } from "@tabler/icons-react";
import { formatPriceWithSymbol } from "@/lib/shared/currency";
import styles from "./styles.module.scss";
import {
  Box as MantineBox,
  List as MantineList,
  Text as MantineText,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";

interface Props {
  items: SearchResultItem[];
  loading: boolean;
  hasSearched: boolean;
  error: boolean;
  retry: () => void;
}

export const DuplicateListings = ({ items, loading, hasSearched, error }: Props) => {
  // Keep the page quiet while the check is running.
  if (loading || !hasSearched) return null;

  if (error) {
    return null;
  }

  // No duplicates found — stay quiet, this is the expected common case.
  if (items.length === 0) return null;

  const sorted = [...items].sort((a, b) => (a.priceusd ?? 0) - (b.priceusd ?? 0));
  const count = sorted.length;
  const label = count === 1 ? "1 duplicate listing found" : `${count} duplicate listings found`;

  const vendorCounts = new Map<number, number>();
  for (const item of sorted) {
    const vid = item.vendor?.id;
    if (vid != null) vendorCounts.set(vid, (vendorCounts.get(vid) ?? 0) + 1);
  }

  return (
    <MantineBox className={styles.duplicateSection}>
      <IconTags className={styles.duplicateIcon} size={16} stroke={1.5} aria-hidden="true" />
      <MantineBox className={styles.duplicateContent}>
        <MantineText className={styles.duplicateCount}>{label}</MantineText>
        <MantineList className={styles.duplicateList}>
          {sorted.map((item) => {
            const vendorId = item.vendor?.id;
            const showCountry =
              vendorId != null && (vendorCounts.get(vendorId) ?? 0) > 1 && item.country;
            const vendorLabel = showCountry
              ? `${item.vendor?.label} (${item.country})`
              : item.vendor?.label;
            return (
              <MantineList.Item key={item.id}>
                <Link
                  href={`/search/product/${item.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.duplicateLink}
                >
                  <MantineText span>
                    Also available on{" "}
                    <MantineText span fw={700}>
                      {vendorLabel}
                    </MantineText>{" "}
                    for{" "}
                    {formatPriceWithSymbol({
                      price: item.price || "0",
                      currencyCode: item.currency || "USD",
                      priceUsd: item.priceusd,
                      roundToFive: false,
                      rawPrice: item.rawPrice,
                    })}
                  </MantineText>
                  <IconExternalLink size={12} />
                </Link>
              </MantineList.Item>
            );
          })}
        </MantineList>
      </MantineBox>
    </MantineBox>
  );
};
